import { UnexpectedTimestampError } from '../appError/AppError.js';
import { ChronoUnit, DateTimeFormatter, Instant, LocalDateTime, ZonedDateTime } from '@js-joda/core';
import { Locale } from '@js-joda/locale_en-us';
export const getSystemOffset = () => ZonedDateTime.now().withFixedOffsetZone().offset();
export const isSameDay = (a, b) => {
    if (!a || !b) {
        return false;
    }
    return LocalDateTime.ofInstant(a)
        .truncatedTo(ChronoUnit.DAYS)
        .equals(LocalDateTime.ofInstant(b).truncatedTo(ChronoUnit.DAYS));
};
export const humanFormatter = DateTimeFormatter.ofPattern('eeee dd, yyyy hh:mm a').withLocale(Locale.ENGLISH);
export const humanizeDuration = (duration) => {
    if (duration.toDays() >= 401)
        return `${Math.floor((duration.toDays() / 365) * 10) / 10} years`; // only plural if >= 1.1 years
    if (duration.toDays() >= 365)
        return `${Math.floor((duration.toDays() / 365) * 10) / 10} year`;
    if (duration.toDays() >= 34)
        return `${Math.floor((duration.toDays() / 30) * 10) / 10} months`; // only plural if >= 1.1 months
    if (duration.toDays() >= 30)
        return `${Math.floor((duration.toDays() / 30) * 10) / 10} month`;
    if (duration.toDays() > 1)
        return `${duration.toDays()} days`;
    if (duration.toDays() === 1)
        return '1 day';
    if (duration.toHours() > 1)
        return `${duration.toHours()} hours`;
    if (duration.toHours() === 1)
        return '1 hour';
    if (duration.toMinutes() > 1)
        return `${duration.toMinutes()} minutes`;
    if (duration.toMinutes() === 1)
        return '1 minute';
    if (duration.seconds() > 1)
        return `${duration.seconds()} seconds`;
    if (duration.seconds() === 1)
        return '1 second';
    if (duration.toMillis() > 1)
        return `${duration.toMillis()} millis`;
    if (duration.toMillis() === 1)
        return '1 milli';
    if (duration.toNanos() > 1)
        return `${duration.toNanos()} nanos`;
    if (duration.toNanos() === 1)
        return '1 nano';
    return 'now';
};
/**
 * Formats a Duration in a compact format for display in tight UI spaces
 * Examples: "0s", "5s", "5m", "5m50s", "2h", "2h30m"
 */
export const formatDurationCompact = (duration) => {
    const totalSeconds = duration.seconds();
    if (totalSeconds === 0)
        return '0s';
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    // If we have hours
    if (hours > 0) {
        if (remainingMinutes === 0 && seconds === 0) {
            return `${hours}h`;
        }
        if (seconds === 0) {
            return `${hours}h${remainingMinutes}m`;
        }
        return `${hours}h${remainingMinutes}m${seconds}s`;
    }
    // If we have minutes
    if (minutes > 0) {
        if (seconds === 0) {
            return `${minutes}m`;
        }
        return `${minutes}m${seconds}s`;
    }
    // Just seconds
    return `${seconds}s`;
};
/**
 * Formats a Duration in a verbose format with proper singular/plural forms
 * Examples: "0 seconds", "5 seconds", "5 minutes", "5 minutes 50 seconds", "2 hours 30 minutes"
 */
export const formatDurationVerbose = (duration) => {
    const totalSeconds = duration.seconds();
    if (totalSeconds === 0)
        return '0 seconds';
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    const parts = [];
    // Add hours
    if (hours > 0) {
        parts.push(hours === 1 ? '1 hour' : `${hours} hours`);
    }
    // Add minutes
    if (remainingMinutes > 0) {
        parts.push(remainingMinutes === 1 ? '1 minute' : `${remainingMinutes} minutes`);
    }
    // Add seconds
    if (seconds > 0) {
        parts.push(seconds === 1 ? '1 second' : `${seconds} seconds`);
    }
    return parts.join(' ');
};
/**
 * Humanizes a Period (ISO-8601 like P1M, P14D, P1Y) into a human-readable billing label.
 * Designed for subscription billing intervals; produces short labels suitable for price display.
 *
 * Examples: "month", "year", "2 weeks", "3 months", "14 days"
 */
export const humanizePeriod = (period) => {
    const years = period.years();
    const months = period.months();
    const days = period.days();
    // Pure year periods
    if (years > 0 && months === 0 && days === 0) {
        return years === 1 ? 'year' : `${years} years`;
    }
    // Pure month periods
    if (months > 0 && years === 0 && days === 0) {
        return months === 1 ? 'month' : `${months} months`;
    }
    // Pure day periods — try to express as weeks when cleanly divisible
    if (days > 0 && years === 0 && months === 0) {
        if (days % 7 === 0) {
            const weeks = days / 7;
            return weeks === 1 ? 'week' : `${weeks} weeks`;
        }
        return days === 1 ? 'day' : `${days} days`;
    }
    // Mixed periods — combine parts
    const parts = [];
    if (years > 0)
        parts.push(years === 1 ? '1 year' : `${years} years`);
    if (months > 0)
        parts.push(months === 1 ? '1 month' : `${months} months`);
    if (days > 0)
        parts.push(days === 1 ? '1 day' : `${days} days`);
    return parts.join(' ') || 'one-time';
};
// Create threshold to handle extreme negative values
const minParseNumberThreshold = Number.MIN_SAFE_INTEGER;
// Timestamps above this threshold are assumed to be in microseconds rather than milliseconds.
// Millisecond timestamps for dates between 2000-2100 fall in the range ~1e12 to ~4e12.
// Microsecond timestamps for that same range fall in ~1e15 to ~4e15.
// A threshold of 1e13 cleanly separates the two.
const microsecondThreshold = 1e13;
export const parseNumber = (timestamp) => {
    try {
        if (timestamp < minParseNumberThreshold) {
            return Instant.ofEpochMilli(minParseNumberThreshold);
        }
        const truncated = Math.trunc(timestamp);
        if (truncated > microsecondThreshold || truncated < -microsecondThreshold) {
            return Instant.ofEpochMicro(truncated);
        }
        let time = Instant.ofEpochMilli(truncated);
        if (truncated !== timestamp) {
            const nanos = Math.trunc((timestamp % 1) * 1e6);
            time = time.plusNanos(nanos);
        }
        return time;
    }
    catch (e) {
        let error;
        if (e instanceof Error) {
            error = e;
        }
        throw UnexpectedTimestampError.create('Failed to parse timestamp', { timestamp, cause: error });
    }
};
